import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import logger from "../lib/logger";
import { accountsApi } from "../lib/accountsApi";
import { getSessionId } from "../lib/session";

const AccountContext = createContext(null);

export function AccountProvider({ children }) {
    const [account, setAccount] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            // Cookie is sent automatically; if the user isn't logged in we just
            // get 401 and stay unauthenticated.
            try {
                const me = await accountsApi.me();
                setAccount(me);
                if (me?.primary_session_id) {
                    localStorage.setItem("ea_session_id", me.primary_session_id);
                }
            } catch (err) {
                if (err?.response?.status !== 401) {
                    logger.warn("/accounts/me failed:", err);
                }
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const persistAuth = useCallback((data) => {
        setAccount(data.account);
        if (data.account?.primary_session_id) {
            localStorage.setItem("ea_session_id", data.account.primary_session_id);
        }
    }, []);

    const login = useCallback(async (email, password) => {
        const data = await accountsApi.login(email, password);
        persistAuth(data);
        return data.account;
    }, [persistAuth]);

    const register = useCallback(async (email, password, consent) => {
        const sid = getSessionId();
        const data = await accountsApi.register(email, password, sid, consent?.date_of_birth, consent?.accepted_terms);
        persistAuth(data);
        return data.account;
    }, [persistAuth]);

    const setPassword = useCallback(async (email, password, consent) => {
        const sid = getSessionId();
        const data = await accountsApi.setPassword(sid, email, password, consent?.date_of_birth, consent?.accepted_terms);
        persistAuth(data);
        return data.account;
    }, [persistAuth]);

    const finishGoogleAuth = useCallback(async (oauth_session_id) => {
        const sid = getSessionId();
        const data = await accountsApi.google(oauth_session_id, sid);
        if (data.needs_consent) return data;
        persistAuth(data);
        return data.account;
    }, [persistAuth]);

    const completeGoogleConsent = useCallback(async (pending_token, date_of_birth, accepted_terms) => {
        const sid = getSessionId();
        const data = await accountsApi.googleComplete(pending_token, date_of_birth, accepted_terms, sid);
        persistAuth(data);
        return data.account;
    }, [persistAuth]);

    const resetPassword = useCallback(async (token, password) => {
        const data = await accountsApi.resetPassword(token, password);
        persistAuth(data);
        return data.account;
    }, [persistAuth]);

    const logout = useCallback(async () => {
        try { await accountsApi.logout(); } catch (err) { logger.warn("logout failed:", err); }
        setAccount(null);
    }, []);

    return (
        <AccountContext.Provider value={{ account, loading, login, register, setPassword, finishGoogleAuth, completeGoogleConsent, resetPassword, logout }}>
            {children}
        </AccountContext.Provider>
    );
}

export const useAccount = () => useContext(AccountContext);
